import { Box, Divider, Typography } from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomCard from 'components/shared/CustomCard/CustomCard';
import CustomDrawer from 'components/shared/CustomDrawer/CustomDrawer';
import CustomModal from 'components/shared/CustomModal/CustomModal';
import CustomStepper from 'components/shared/CustomStepper/CustomStepper';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { Formik } from 'formik';
import {
  DealType,
  IBrandedEntertainment,
  ICommercialEndorsement,
  IEquityInvestment,
  IEventAppearance,
  IEventOwned,
  ILicensingIP,
  PIPELINE_MAP,
} from 'model/deals.model';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { modalReducer } from 'reducers/modalReducer';
import { ClientService } from 'service/client.service';
import { DealsService } from 'service/deals.service';
import { HuddleClientService } from 'service/huddle-client.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import DealsAddProductFormField from './components/DealsAddProductFormField';
import { DealSelectionModal } from './components/DealSelectionModal';
import DealsPriceBookField from './components/DealsPriceBookField';
import { BrandedEntertainment } from './forms/BrandedEntertainment';
import { CommercialEndorsement } from './forms/CommercialEndorsement';
import { ContentProduction } from './forms/ContentProduction';
import { EquityInvestment } from './forms/EquityInvestment';
import { EventAppearance } from './forms/EventAppearance';
import { EventOwned } from './forms/EventOwned';
import { LicensingIP } from './forms/LicensingIP';

type DealsDetailsType = Partial<
  ICommercialEndorsement &
    IEventAppearance &
    IEventOwned &
    ILicensingIP &
    IEquityInvestment &
    IBrandedEntertainment &
    any
>;

export interface FormValues {
  pipelineType: DealType | '';
  priceBookId: string;
  dealDetails: DealsDetailsType;
}

const DealFormMap = {
  'Commercial/Endorsement': <CommercialEndorsement />,
  Event_Appearance: <EventAppearance />,
  Event_Owned: <EventOwned />,
  'Content/Production': <ContentProduction />,
  'Licensing/IP': <LicensingIP />,
  'Equity/Investment': <EquityInvestment />,
  'Branded Entertainment': <BrandedEntertainment />,
};

const METADATA_FIELDS = [
  'dealId',
  'id',
  'createdDate',
  'createdAt',
  'createdBy',
  'createdByName',
  'lastModifiedDate',
  'updatedAt',
];

type NewDealFormProps = {
  dealId?: string;
  isEdit?: boolean;
  deal?: any;
  onDealCreated?: (changesMade?: boolean) => void;
};

export const NewDealForm = ({
  dealId: dealIdProp,
  isEdit = false,
  deal,
  onDealCreated,
}: NewDealFormProps) => {
  const dealId = dealIdProp;
  const [recordType, setRecordType] = useState({});
  const [createdDealId, setCreatedDealId] = useState<string | null>(null);
  const [initialFormValues, setInitialFormValues] = useState<FormValues | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});

  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    selectedDealType: !isEdit,
    priceBookChange: false,
    drawer: isEdit,
  });

  const accessToken = useMemo(() => {
    const SFToken = sessionStorage.getItem('sfToken');
    return SFToken ? JSON.parse(SFToken) : {};
  }, []);

  const transformApiToForm = (dealData?: any): FormValues => {
    const apiData = dealData || {};
    let rawPipelineType = apiData.pipelineType || apiData.dealType || '';

    let pipelineType: DealType | '' = '';
    if (rawPipelineType) {
      if (PIPELINE_MAP[rawPipelineType]) {
        pipelineType = PIPELINE_MAP[rawPipelineType] as DealType;
      } else if (Object.values(PIPELINE_MAP).includes(rawPipelineType)) {
        pipelineType = rawPipelineType as DealType;
      } else {
        const normalizedKey = Object.keys(PIPELINE_MAP).find(
          (key) => key.toLowerCase() === rawPipelineType.toLowerCase(),
        );
        if (normalizedKey) {
          pipelineType = PIPELINE_MAP[normalizedKey] as DealType;
        } else {
          const normalizedValue = Object.values(PIPELINE_MAP).find(
            (value) => value.toLowerCase() === rawPipelineType.toLowerCase(),
          );
          pipelineType = (normalizedValue || rawPipelineType) as DealType;
        }
      }
    }

    const priceBookId = apiData.priceBookId || apiData.pricebookId || apiData.pricebook2Id || '';
    const {
      pipelineType: _,
      priceBookId: __,
      pricebookId: ___,
      pricebook2Id: ____,
      dealType: _____,
      ...dealDetails
    } = apiData;

    return {
      pipelineType: pipelineType || '',
      priceBookId: priceBookId || '',
      dealDetails: dealDetails as DealsDetailsType,
    };
  };

  const data = useMemo(() => transformApiToForm(deal), [deal]);

  const initialValues = useMemo<FormValues>(() => {
    if (isEdit) {
      return {
        pipelineType: data.pipelineType as DealType,
        dealDetails: data.dealDetails,
        priceBookId: data.priceBookId,
      };
    }
    return {
      pipelineType: '',
      priceBookId: '',
      dealDetails: {} as DealsDetailsType,
    };
  }, [isEdit, data]);

  const initialActiveStep = useMemo(() => {
    return 0;
  }, []);

  useEffect(() => {
    setActiveStep(initialActiveStep);
  }, [initialActiveStep]);

  useEffect(() => {
    if (isEdit && !initialFormValues && initialValues.pipelineType) {
      setInitialFormValues(JSON.parse(JSON.stringify(initialValues)));
    }
  }, [isEdit, initialValues.pipelineType, initialFormValues]);

  useEffect(() => {
    const getRecordType = async () => {
      try {
        const res = await ClientService.getDealType();
        setRecordType(res);
      } catch (err) {
        console.error('Failed to fetch record types:', err);
      }
    };
    getRecordType();
  }, []);

  const hasFormChanged = (initial: FormValues | null, current: FormValues): boolean => {
    if (!initial) return true;

    if (initial.pipelineType !== current.pipelineType) return true;
    if (initial.priceBookId !== current.priceBookId) return true;

    const initialDetails = { ...initial.dealDetails };
    const currentDetails = { ...current.dealDetails };

    METADATA_FIELDS.forEach((field) => {
      delete initialDetails[field as keyof typeof initialDetails];
      delete currentDetails[field as keyof typeof currentDetails];
    });

    return JSON.stringify(initialDetails) !== JSON.stringify(currentDetails);
  };

  const normalizeDealType = (type: DealType | ''): string => {
    if (!type) return '';
    const entry = Object.entries(PIPELINE_MAP).find(([_, value]) => value === type);
    return entry ? entry[0] : type;
  };

  const getSelectedProducts = (products: any[]) => {
    return products.filter(
      (product: any) =>
        product?.Quantity &&
        product?.Quantity !== '' &&
        product?.Quantity !== null &&
        product?.Quantity !== undefined,
    );
  };

  const productsHaveChanged = (initialProducts: any[], currentProducts: any[]): boolean => {
    const normalizeProduct = (p: any) => ({
      PricebookEntryId: p?.PricebookEntryId || p?.id,
      Quantity: p?.Quantity,
      UnitPrice: p?.UnitPrice || p?.SalesPrice,
      ServiceDate: p?.ServiceDate || p?.Date,
    });

    const initialStr = JSON.stringify(initialProducts.map(normalizeProduct));
    const currentStr = JSON.stringify(currentProducts.map(normalizeProduct));
    return initialStr !== currentStr;
  };

  const handleProductSubmit = async (values: FormValues) => {
    const products = values.dealDetails.products || [];
    const currentDealId = dealId || createdDealId;

    if (!currentDealId) {
      completeOperation('create', 'Deal ID is required to add products', true);
      return;
    }

    const selectedProducts = getSelectedProducts(products);

    if (isEdit && initialFormValues) {
      const initialProducts = initialFormValues.dealDetails?.products || [];
      const initialSelectedProducts = getSelectedProducts(initialProducts);

      if (!productsHaveChanged(initialSelectedProducts, selectedProducts)) {
        completeOperation('create', 'No changes to save');
        modalDispatch({ type: 'CLOSE', payload: 'drawer' });
        onDealCreated?.(false);
        return;
      }
    }

    try {
      startOperation('create', 'Adding products to deal...');

      await DealsService.updateDeal(currentDealId, {
        products: selectedProducts.map((product: any) => ({
          ...product,
          OpportunityId: currentDealId,
        })),
      });

      completeOperation('create', 'Products added successfully');
      modalDispatch({ type: 'CLOSE', payload: 'drawer' });
      onDealCreated?.(true);
    } catch (err: any) {
      completeOperation('create', err?.response?.data?.message || 'Failed to save products', true);
    }
  };

  const handleDealSubmit = async (values: FormValues) => {
    const isHuddle = window.location.pathname.includes('huddle');
    const { pipelineType, priceBookId, dealDetails } = values;

    if (isEdit && dealId && initialFormValues) {
      if (!hasFormChanged(initialFormValues, values)) {
        if (priceBookId && priceBookId !== 'none') {
          setActiveStep(2);
        } else {
          modalDispatch({ type: 'CLOSE', payload: 'drawer' });
          onDealCreated?.(false);
        }
        return;
      }
    }

    const { expectCloseDate, ...restDealDetails } = dealDetails;

    const updateableFields = { ...restDealDetails };
    METADATA_FIELDS.forEach((field) => {
      delete updateableFields[field as keyof typeof updateableFields];
    });

    const payload = {
      ...updateableFields,
      closeDate: restDealDetails.closeDate || expectCloseDate || undefined,
      pipelineType: normalizeDealType(pipelineType),
      pricebookId: priceBookId !== 'none' ? priceBookId : undefined,
    };

    const operationType = isEdit && dealId ? 'update' : 'create';

    try {
      startOperation(operationType, isEdit && dealId ? 'Updating deal...' : 'Creating deal...');
      let response;

      if (isEdit && dealId) {
        if (isHuddle) {
          const recordTypeName = Utilities.getMatchingRecordTypeKey(
            pipelineType,
            recordType,
          )?.replace(' Deal', '');
          response = await HuddleClientService.editHuddleDeal(dealId, {
            ...payload,
            recordTypeName,
            access_token: accessToken?.access_token,
          });
        } else {
          try {
            response = await DealsService.updateDeal(dealId, payload);
          } catch (dealsError) {
            response = await ClientService.editDeal(dealId, {
              ...payload,
              access_token: accessToken?.access_token,
            });
          }
        }
      } else {
        response = await DealsService.createDeal(payload);
        if (response?.id || response?.dealId) {
          setCreatedDealId(response.id || response.dealId);
        }
      }

      if (priceBookId && priceBookId !== 'none') {
        setActiveStep(2);
        completeOperation(operationType, `Deal ${isEdit ? 'updated' : 'created'} successfully`);
      } else {
        completeOperation(operationType, `Deal ${isEdit ? 'updated' : 'created'} successfully`);
        modalDispatch({ type: 'CLOSE', payload: 'drawer' });
        onDealCreated?.(true);
      }
    } catch (err: any) {
      completeOperation(
        operationType,
        err?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} deal`,
        true,
      );
    }
  };

  const handleCloseDrawer = (changesMade: boolean = false) => {
    setActiveStep(0);
    setCompleted({});
    setCreatedDealId(null);
    setInitialFormValues(null);
    modalDispatch({ type: 'CLOSE', payload: 'drawer' });
    onDealCreated?.(changesMade);
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values) => {
        if (activeStep === 0) {
          setActiveStep(1);
        } else if (activeStep === 1) {
          await handleDealSubmit(values);
        } else if (activeStep === 2) {
          await handleProductSubmit(values);
        }
      }}
    >
      {({ values, setFieldValue, handleSubmit, resetForm }) => {
        const handleClose = () => {
          resetForm();
          handleCloseDrawer();
        };

        const isStep0Valid = () => {
          if (activeStep !== 0) return true;

          if (!values.pipelineType) {
            return false;
          }

          const dealDetails = values.dealDetails || {};
          const requiredFields = [
            'dealDetails.name',
            'dealDetails.clientName',
            'dealDetails.ownerName',
            'dealDetails.stageName',
          ];

          return requiredFields.every((fieldName) => {
            const fieldKey = fieldName.replace('dealDetails.', '');
            const value = dealDetails[fieldKey];
            return value !== undefined && value !== null && value !== '';
          });
        };

        const isContinueDisabled = !isStep0Valid();

        return (
          <>
            <DealSelectionModal
              isModalOpen={modalState.selectedDealType}
              modalDispatch={modalDispatch}
              onSelect={(type) => setFieldValue('pipelineType', type)}
            />

            <CustomDrawer state={modalState.drawer} onClose={handleClose}>
              <Box
                sx={{
                  width: { xs: '427px', xl: '527px' },
                  position: 'relative',
                  overflowY: 'scroll',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: zeplinColor.Neutral1,
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: { xs: '24px 24px 24px 24px', xl: '40px 24px 24px 24px' },
                    }}
                  >
                    <Typography variant='responsiveHeading2'>
                      {isEdit
                        ? `Edit Deal: ${values.pipelineType}`
                        : `New Deal: ${values.pipelineType}`}
                    </Typography>
                    <img
                      src='/assets/close-icon.svg'
                      width='24px'
                      height='24px'
                      alt='close'
                      style={{ cursor: 'pointer' }}
                      onClick={handleClose}
                    />
                  </Box>
                  <Divider
                    component='div'
                    sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
                  />
                  <CustomStepper
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    setCompleted={setCompleted}
                    completed={completed}
                  />
                </Box>

                {activeStep === 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: '16px', xl: '24px' },
                      flexDirection: 'column',
                      padding: { xs: '16px 24px 24px', xl: '24px' },
                    }}
                  >
                    {DealFormMap[values.pipelineType as keyof typeof DealFormMap]}
                  </Box>
                )}

                {activeStep === 1 && (
                  <Box
                    sx={{ display: 'flex', gap: '24px', flexDirection: 'column', padding: '24px' }}
                  >
                    <DealsPriceBookField
                      value={values.priceBookId}
                      handleInputChange={(field = 'priceBookId', val: any) =>
                        setFieldValue(field, val)
                      }
                      setPricebookName={() => {}}
                      modal={false}
                    />
                  </Box>
                )}

                {activeStep === 2 && (
                  <Box
                    sx={{ display: 'flex', gap: '24px', flexDirection: 'column', padding: '24px' }}
                  >
                    <DealsAddProductFormField
                      handleInputChange={(field, val, index) => {
                        if (index !== undefined && field !== 'products') {
                          const currentProducts = values.dealDetails?.products || [];
                          const updatedProducts = [...currentProducts];
                          updatedProducts[index] = {
                            ...updatedProducts[index],
                            [field]: val,
                          };
                          setFieldValue('dealDetails.products', updatedProducts);
                        } else {
                          setFieldValue(`dealDetails.${field}`, val);
                        }
                      }}
                      value={{
                        pricebook2Id: values.priceBookId,
                        products: values.dealDetails?.products,
                      }}
                      opportunityId={dealId || createdDealId || ''}
                      pricebookName={values.priceBookId}
                      key={values.priceBookId}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    width: '100%',
                    position: 'sticky',
                    bottom: '0px',
                    margin: 'auto',
                    zIndex: '100',
                    padding: '24px',
                    backgroundColor: zeplinColor.Neutral1,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: '24px' }}>
                    {activeStep > 0 && (
                      <CustomButton
                        disabled={false}
                        onClick={() => setActiveStep((prev) => prev - 1)}
                        variant='outlined'
                        color='customSecondary'
                        style={{
                          borderRadius: '14px',
                          height: 56,
                          width: '100%',
                          border: `1px solid ${zeplinColor.BasicBlack}`,
                          '&.Mui-focusVisible': { backgroundColor: zeplinColor.Neutral1 },
                          flex: 1,
                        }}
                        textStyle={{
                          color: zeplinColor.BasicBlack,
                        }}
                        text='Back'
                      />
                    )}
                    <CustomButton
                      onClick={() => {
                        if (activeStep === 0) {
                          setActiveStep(1);
                        } else {
                          handleSubmit();
                        }
                      }}
                      variant='contained'
                      style={{
                        borderRadius: '14px',
                        height: 56,
                        width: '100%',
                        '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                        flex: activeStep > 0 ? 3 : 1,
                      }}
                      text={activeStep === 2 ? 'Finish' : 'Continue'}
                      disabled={isContinueDisabled}
                    />
                  </Box>
                </Box>
              </Box>
            </CustomDrawer>

            <CustomModal handleClose={() => {}} open={modalState.priceBookChange}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CustomCard
                  styles={{
                    borderRadius: '14px',
                    padding: '24px',
                    backgroundColor: zeplinColor.Neutral1,
                    width: '375px',
                    boxShadow: 'rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                        Confirm Price Book Change
                      </Typography>
                      <Typography variant='body2' color={zeplinColor.BasicBlack}>
                        You have changed the price book for this deal. This action permanently
                        deletes all the products from the deal. Are you sure you want to continue?
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '16px' }}>
                      <Box
                        onClick={() => modalDispatch({ type: 'CLOSE', payload: 'priceBookChange' })}
                        sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                      >
                        <CustomCard
                          styles={{
                            borderRadius: '14px',
                            padding: '14px 32px',
                            backgroundColor: zeplinColor.Neutral1,
                            border: `1px solid ${zeplinColor.BasicBlack}`,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            flex: 1,
                          }}
                        >
                          <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                            Cancel
                          </Typography>
                        </CustomCard>
                      </Box>
                      <Box
                        onClick={() => {
                          // TODO: Implement price book change confirmation
                          modalDispatch({ type: 'CLOSE', payload: 'priceBookChange' });
                        }}
                        sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                      >
                        <CustomCard
                          styles={{
                            borderRadius: '14px',
                            padding: '14px 32px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            flex: 1,
                          }}
                          aria-label='button'
                        >
                          <Typography
                            variant='buttonMedium'
                            color={zeplinColor.Neutral1}
                            sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                          >
                            Yes, Save
                          </Typography>
                        </CustomCard>
                      </Box>
                    </Box>
                  </Box>
                </CustomCard>
              </Box>
            </CustomModal>

            <CustomAlert
              open={alertState.status !== 'idle'}
              handleClose={() => setAlertState(initialAlertState)}
              message={getAlertContent(alertState)}
              error={alertState.status === 'error'}
            />
          </>
        );
      }}
    </Formik>
  );
};
